import { P2GText } from '@parcel2go/core-ui';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { toDisplayCurrency } from '~/lib/prices';
import { Currency } from '~/lib/types/basket-types';

type SectionProps = {
  currency: Currency;
  dataTestId: string;
  value: number;
  textColor?: ComponentProps<typeof P2GText>['colour'];
  minimumFractionDigits?: number;
  excVat?: boolean;
  incPlus?: boolean;
  incAsterisk?: boolean;
  fontWeight?: number | 'bold' | 'bolder' | 'lighter' | 'normal';
  variant?:
    | 'oversized'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'label'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'smallText'
    | 'button'
    | 'overline'
    | 'hyperlink';
};

function Price({
  currency,
  value,
  dataTestId,
  textColor,
  minimumFractionDigits,
  excVat,
  incPlus,
  fontWeight,
  incAsterisk,
  variant
}: SectionProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);

  if (value === 0) {
    return (
      <P2GText
        variant={variant}
        fontWeight={fontWeight || 700}
        colour={textColor ?? 'primary'}
        dataTestId={dataTestId}
        whiteSpace="nowrap"
      >
        {t('PRICE_FREE', { defaultValue: 'Free' })}
        {incAsterisk && ' *'}
      </P2GText>
    );
  }

  return (
    <P2GText variant={variant} dataTestId={dataTestId} colour={textColor} fontWeight={fontWeight} whiteSpace="nowrap">
      {incPlus && value > 0 && '+ '}
      {excVat
        ? t('PRICE_EXC_VAT', {
            defaultValue: '{{price}} exc VAT',
            price: toDisplayCurrency(currency, value, minimumFractionDigits)
          })
        : toDisplayCurrency(currency, value, minimumFractionDigits)}
      {incAsterisk && ' *'}
    </P2GText>
  );
}

export default Price;
